// 📊 Dashboard Routes - Rutas del módulo dashboard
import type { RouteRecordRaw } from 'vue-router'

const dashboardRoutes: RouteRecordRaw[] = [
  {
    path: '/dashboard',
    name: 'DashboardModule',
    component: () => import('@/layouts/DashboardLayout.vue'),
    meta: {
      title: 'Dashboard',
      requiresAuth: true,
      roles: ['super_admin', 'administration_manager', 'group_manager', 'field_supervisor', 'route_operator', 'agency_employee', 'department_head'],
      layout: 'dashboard',
      permissions: ['dashboard.view'],
      breadcrumb: [{ name: 'Dashboard' }],
      cache: true,
    },
    children: [
      // P13-V7B-MIN C2 — `roles` is declared EXPLICITLY on these two children.
      //
      // The parent record above grants all seven canonical roles, and a child without its own
      // `roles` inherits that grant (Vue Router merges meta across every matched record), so the four
      // demo roles could reach these views by direct URL. `AnalyticsView.vue` fabricates part of its
      // content — `scatterDatasets` builds correlation points from `Math.random()`
      // (AnalyticsView.vue:524-529) — which is a mock presented as analysis, i.e. exactly the
      // "visible promise ≠ real capability" failure C2 must remove. Reporting truth is owned by
      // V5A-MIN and is NOT implemented here; the sibling `business-intelligence` child already uses
      // this same explicit-`roles` shape. super_admin keeps its existing access, so no legitimate
      // capability is withdrawn.
      {
        path: 'analytics',
        name: 'DashboardAnalytics',
        component: () => import('@/views/AnalyticsView.vue'),
        meta: {
          title: 'Analytics',
          roles: ['super_admin'],
          permissions: ['analytics.view'],
          breadcrumb: [
            { name: 'Dashboard', path: '/dashboard' },
            { name: 'Analytics' },
          ],
          analytics: {
            category: 'Dashboard',
            action: 'view',
            label: 'analytics',
          },
        },
      },
      {
        path: 'kpi',
        name: 'DashboardKPI',
        component: () => import('@/views/AnalyticsView.vue'),
        meta: {
          title: 'KPI Dashboard',
          roles: ['super_admin'],
          permissions: ['kpi.view'],
          breadcrumb: [
            { name: 'Dashboard', path: '/dashboard' },
            { name: 'KPIs' },
          ],
        },
      },
      {
        path: 'business-intelligence',
        name: 'BusinessIntelligenceExecutive',
        component: () => import('@/views/dashboards/BusinessIntelligenceExecutive.vue'),
        meta: {
          title: 'Business Intelligence Ejecutivo',
          roles: ['super_admin'],
          permissions: ['super_admin'],
          breadcrumb: [
            { name: 'Dashboard', path: '/dashboard' },
            { name: 'Business Intelligence' },
          ],
          analytics: {
            category: 'Dashboard',
            action: 'view',
            label: 'business-intelligence',
          },
        },
      },
      {
        path: 'reports',
        name: 'DashboardReports',
        component: () => import('@/views/dashboard/ReportsView.vue'),
        meta: {
          title: 'Reports',
          permissions: ['reports.view'],
          breadcrumb: [
            { name: 'Dashboard', path: '/dashboard' },
            { name: 'Reports' },
          ],
        },
        children: [
          {
            path: ':reportId',
            name: 'ReportDetail',
            component: () => import('@/views/dashboard/ReportDetailView.vue'),
            meta: {
              title: 'Report Detail',
              permissions: ['reports.view'],
              breadcrumb: [
                { name: 'Dashboard', path: '/dashboard' },
                { name: 'Reports', path: '/dashboard/reports' },
                { name: 'Detail' },
              ],
            },
          },
        ],
      },
      {
        path: 'organizational-reports',
        name: 'OrganizationalReports',
        component: () => import('@/views/reports/OrganizationalReportsCenter.vue'),
        meta: {
          title: 'Centro de Reportes Empresarial',
          roles: ['super_admin', 'group_manager'],
          breadcrumb: [
            { name: 'Dashboard', path: '/dashboard' },
            { name: 'Reportes Organizacionales' },
          ],
          analytics: {
            category: 'Reports',
            action: 'view',
            label: 'organizational-reports',
          },
        },
      },
      {
        path: 'settings',
        name: 'DashboardSettings',
        component: () => import('@/views/dashboard/SettingsView.vue'),
        meta: {
          title: 'Dashboard Settings',
          permissions: ['dashboard.settings'],
          breadcrumb: [
            { name: 'Dashboard', path: '/dashboard' },
            { name: 'Settings' },
          ],
        },
      },
      // WS4 · H2 (MGI-PILOT-W2-DEFERRED-CLIENT-CAPABILITY-PERIMETER-CONVERGENCE-WS4): `roles` declared
      // EXPLICITLY. The parent record grants all seven canonical roles and `permissions` is not consulted
      // by the canonical guard, so every delivered pilot identity could reach a page whose only data
      // producer — the durable notification reader — the backend now refuses it. The page admits exactly
      // the roles the backend admits to that reader (services/notifications.admission.ts); the same
      // explicit-`roles` shape the V7B-MIN C2 siblings above use.
      {
        path: 'notifications',
        name: 'DashboardNotifications',
        component: () => import('@/views/dashboard/NotificationsView.vue'),
        meta: {
          title: 'Notifications',
          roles: ['super_admin', 'administration_manager', 'group_manager'],
          permissions: ['notifications.view'],
          breadcrumb: [
            { name: 'Dashboard', path: '/dashboard' },
            { name: 'Notifications' },
          ],
          cache: false,
        },
      },
    ],
  },
]

export default dashboardRoutes
